/**
 * The default FresherBot wordmark.
 *
 * /fresherbot-logo.png has near-black text baked into the image, so on a dark
 * canvas it reads as black-on-black. There is no light variant of the asset,
 * so in dark mode we render the wordmark as TEXT instead — which also stays
 * crisp at any size and needs no second file to keep in sync.
 *
 * The colour comes from --fb-ink (near-white in dark) rather than from either
 * `C` palette on purpose: this renders inside components that import AppRail's
 * C *and* components that import dashboard/theme's C, and the latter is still
 * a light-only palette. Reading the CSS variable directly sidesteps that.
 *
 * This is only the DEFAULT mark. White-label logos are resolved by the callers
 * via useBranding and left alone — a partner's uploaded logo is their asset.
 */
import { useDarkMode } from '../hooks/useDarkMode';

export function FresherbotLogo({
  height = 30,
  className,
}: {
  /** Rendered height of the image; the text form is scaled to match it. */
  height?: number;
  className?: string;
}) {
  const { isDark } = useDarkMode();

  if (isDark) {
    return (
      <span
        className={className}
        style={{
          fontWeight: 800,
          letterSpacing: '-0.02em',
          // The PNG is mostly padding around the wordmark, so matching its box
          // height 1:1 would render the text noticeably larger than the logo
          // it replaces.
          fontSize: Math.round(height * 0.72),
          lineHeight: 1,
          color: 'var(--fb-ink, #ffffff)',
          whiteSpace: 'nowrap',
        }}
      >
        FresherBot
      </span>
    );
  }

  return (
    <img
      src="/fresherbot-logo.png"
      alt="FresherBot"
      className={className}
      style={{ height, width: 'auto', objectFit: 'contain' }}
    />
  );
}
